import { providerDisplayName } from "../../shared/providers";
import { providerFromString, type ProviderId } from "../brands";
import type { PlacementReadiness } from "./loopDependencies";

/** Client-side shapes for placements and per-provider performance; mirrors src/worker/placements.ts. */

export type PlacementStatus = "planned" | "active" | "retired";

export type PlacementModel = {
  id: string;
  loopId: string;
  provider: ProviderId;
  agentName: string | null;
  environment: string | null;
  status: PlacementStatus;
  definitionId: string | null;
  startedAt: string | null;
  endedAt: string | null;
  note: string | null;
  createdBy: { type: "user" | "agent" | "system"; id: string | null };
  createdAt: string;
  updatedAt: string;
  /** On a planned placement: how many of the loop's dependencies the target has confirmed. Absent until the worker reports it. */
  readiness?: PlacementReadiness | null;
};

export type ProviderSummaryModel = {
  provider: ProviderId;
  runs: number;
  resolved: number;
  successes: number;
  failures: number;
  successRate: number | null;
  medianDurationMs: number | null;
  costUsd: number;
  costReportedRuns: number;
  firstRunAt: string | null;
  lastRunAt: string | null;
};

export type ProviderBreakdownModel = {
  providers: ProviderSummaryModel[];
  comparable: boolean;
  best: ProviderId | null;
  bestBasis: "reliability" | "duration" | null;
  unattributedRuns: number;
};

export type PlacementsResponse = {
  placements: PlacementModel[];
  current: PlacementModel[];
  observed: ProviderBreakdownModel;
  window: { days: number; since: string; through: string };
};

export type HandoffResponse = { placement: PlacementModel; handoff: string; created: boolean };

export type RunsOnLine = { kind: "declared" | "observed"; provider: ProviderId; agentName: string | null; since: string | null };

export function providerName(id: ProviderId): string {
  return providerDisplayName(id);
}

/**
 * What the "Runs on" card shows: every active placement as a declared home, then any provider that
 * reported a run in the window without a matching active placement (truth beside declaration).
 * With nothing declared, the most recently observed provider stands in.
 */
export function runsOnLines(current: PlacementModel[], observed: ProviderSummaryModel[]): RunsOnLine[] {
  const lines: RunsOnLine[] = current.filter((placement) => placement.status === "active").map((placement) => ({ kind: "declared", provider: placement.provider, agentName: placement.agentName, since: placement.startedAt }));
  const declared = new Set(lines.map((line) => line.provider));
  const byRecency = [...observed].sort((a, b) => (b.lastRunAt ?? "").localeCompare(a.lastRunAt ?? ""));
  if (!lines.length) {
    const latest = byRecency[0];
    return latest ? [{ kind: "observed", provider: latest.provider, agentName: null, since: latest.lastRunAt }] : [];
  }
  for (const summary of byRecency) {
    if (declared.has(summary.provider)) continue;
    lines.push({ kind: "observed", provider: summary.provider, agentName: null, since: summary.lastRunAt });
  }
  return lines;
}

/** Planned placements, oldest first: the moves still waiting for their first run. */
export function plannedMoves(placements: PlacementModel[]): PlacementModel[] {
  return placements.filter((placement) => placement.status === "planned").sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Facet counts for the Loops list: loops by the provider of their last run, most common first. */
export function providerFacets(loops: Array<{ performance: { lastRunProvider: string | null }; runsOn?: { provider: string } | null }>): Array<{ provider: ProviderId; count: number }> {
  const counts = new Map<ProviderId, number>();
  for (const loop of loops) {
    const provider = providerFromString(loop.runsOn?.provider ?? loop.performance.lastRunProvider);
    if (!provider) continue;
    counts.set(provider, (counts.get(provider) ?? 0) + 1);
  }
  return [...counts.entries()].map(([provider, count]) => ({ provider, count })).sort((a, b) => b.count - a.count || providerName(a.provider).localeCompare(providerName(b.provider)));
}

/** The one quiet sentence under Performance by agent; null until two providers both qualify. */
export function comparisonLine(breakdown: ProviderBreakdownModel, days: number): string | null {
  if (!breakdown.comparable) return null;
  const qualifying = breakdown.providers.filter((summary) => summary.resolved >= 3);
  if (qualifying.length < 2) return null;
  const best = breakdown.best ? qualifying.find((summary) => summary.provider === breakdown.best) ?? null : null;
  const ordered = best ? [best, ...qualifying.filter((summary) => summary !== best)] : qualifying;
  const [first, second] = ordered;
  const samples = `${first.resolved} and ${second.resolved} resolved`;
  const window = `over the last ${days} days`;
  if (best && breakdown.bestBasis === "reliability") return `${providerName(best.provider)} has the higher reliability ${window} (${samples}).`;
  const pair = `${providerName(first.provider)} and ${providerName(second.provider)}`;
  if (best && breakdown.bestBasis === "duration") return `${pair} share the same reliability ${window} (${samples}); ${providerName(best.provider)} has the shorter median runtime.`;
  return `${pair} share the same reliability and median runtime ${window} (${samples}).`;
}
